import validationService from '@/services/validation.service';
import { FormControl, FormErrorMessage } from '@chakra-ui/react';
import { Box, Flex, InlineAlert, Input, Text, Image, Button } from '@cmpsr/components';
import { useState } from 'react';
import useSignUpController from './sign-up.controller';
import DarkFeaturesSection from '../ppra/components/ppra-payment-page/components/DarkFeaturesSection';

export default function SignUpPage() {
  const { form, handleSubmit } = useSignUpController();
  const [darkSectionShouldScroll, setDarkSectionShouldScroll] = useState(false);

  return (
    <Box width="100%" background="white" display="flex" flexDirection="column" paddingBottom={0} position="relative">
      <Box
        position="relative"
        zIndex={2}
        width="100%"
        display="flex"
        flexDirection={{ base: 'column', md: 'row' }}
        minHeight={{ base: 'auto', md: '100vh' }}
      >
        {/* Left Column - Form Content */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          px={{ base: '24px', md: '40px' }}
          py={{ base: '40px', md: '0' }}
        >
          {/* Logo with beta */}
          <Box
            display={{ base: 'none', md: 'flex' }}
            justifyContent="flex-start"
            alignItems="center"
            position="absolute"
            top="40px"
            left="40px"
          >
            <Image src="/images/logo-beta.svg" alt="Catch Beta" w="143px" h="auto" flexShrink={0} />
          </Box>

          <Flex
            direction="column"
            gap={{ base: 5, md: 8 }}
            width="100%"
            maxWidth={{ base: '100%', md: '500px', lg: '580px' }}
            pt={{ base: '50px', md: '0' }}
          >
            <Text
              fontSize={{ base: '1.75rem', md: '2.5rem', lg: '3.125rem' }}
              fontWeight="400"
              lineHeight="120%"
              textAlign={{ base: 'center', md: 'left' }}
            >
              Catch members can{' '}
              <Text as="span" fontWeight="bold" fontSize="inherit">
                reduce their cancer risk up to 60%
              </Text>
            </Text>

            <Flex
              as="form"
              onSubmit={handleSubmit}
              direction="column"
              gap={{ base: 4, md: 6 }}
              p={{ base: '24px', md: '0' }}
              borderRadius={{ base: '12px', md: '0' }}
              backgroundColor={{ base: '#F0F5F6', md: 'transparent' }}
            >
              <FormControl id="email" isInvalid={!!form.formState.errors.email}>
                <Input
                  id="email"
                  aria-label="Email"
                  variant="outline"
                  placeholder="Enter email address"
                  isInvalid={!!form.formState.errors.email}
                  autoFocus
                  {...form.register('email', {
                    required: 'Please type an email address',
                    validate: (v: string) => {
                      const isValid = validationService.validateEmail(v);
                      return isValid || 'Please provide a valid email address';
                    },
                  })}
                  height="48px"
                  borderRadius="6px"
                  border="1px solid #A9A69F"
                  backgroundColor="#FDFDFF"
                  padding="14.25px 17px"
                  fontSize="16px"
                  _hover={{
                    borderColor: '#A9A69F',
                  }}
                  _focus={{
                    borderColor: '#635bff',
                    boxShadow: '0 0 0 1px #635bff',
                    outline: 'none',
                  }}
                />
                {form.formState.errors.email?.message && (
                  <FormErrorMessage mt="spacer-2">
                    <InlineAlert status="error">
                      <InlineAlert.Icon />
                      <InlineAlert.Message>{form.formState.errors.email.message}</InlineAlert.Message>
                    </InlineAlert>
                  </FormErrorMessage>
                )}
              </FormControl>

              <Button
                type="submit"
                isDisabled={!!form.formState.errors.email || form.formState.isSubmitting}
                isLoading={form.formState.isSubmitting}
                width="100%"
                height="50px"
                borderRadius="9999px"
                backgroundColor="#7CF1C7"
                color="black"
                fontWeight="500"
                fontSize="16px"
                _hover={{
                  backgroundColor: '#6CE0B6',
                }}
                _active={{
                  backgroundColor: '#5CD0A6',
                }}
              >
                Get Started
              </Button>
            </Flex>
          </Flex>
        </Box>

        {/* Right Column - Dark Features Section */}
        <Box
          flex={{ base: 'none', md: 1 }}
          width={{ base: '100%', md: 'auto' }}
          display="block"
          position={{ base: 'relative', md: darkSectionShouldScroll ? 'relative' : 'sticky' }}
          top={{ base: 'auto', md: darkSectionShouldScroll ? 'auto' : '0' }}
          height={{ base: 'auto', md: darkSectionShouldScroll ? 'auto' : '100vh' }}
        >
          <DarkFeaturesSection onContentHeightChange={setDarkSectionShouldScroll} />
        </Box>
      </Box>
    </Box>
  );
}
